import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { TableMap, columnResizingPluginKey } from 'prosemirror-tables';

import { TABLE_TOUCH_EDGE_PX, columnOf, domCellAround, setColumnWidth, touchedEdgeCell } from './table-touch-resize';

// Double-click (double-tap on a touch screen) on a column border fits the column on its left to its
// contents: as wide as its widest cell needs without wrapping, like the Home List table. The width is
// stored in the cells' colwidth like a drag resize, so it is saved with the note.

const CELL_MIN_WIDTH = 25;
const DOUBLE_TAP_MS = 400;
const TAP_SLOP_PX = 10;
/** prosemirror-tables' mouse handle width (Tiptap's default). */
const MOUSE_EDGE_PX = 5;

export const tableColumnAutoFitKey = new PluginKey('tableColumnAutoFit');

function tableDomOf(view: EditorView, tableStart: number): HTMLTableElement | null {
  let dom: Node | null = view.domAtPos(tableStart).node;
  while (dom && dom.nodeName !== 'TABLE') dom = dom.parentNode;
  return dom as HTMLTableElement | null;
}

/**
 * The width the column of the cell at `cellPos` needs for its widest cell on one line (padding and
 * borders included). Cells spanning several columns are left out. Measured on copies of the cells in a
 * hidden single-column table next to the editor (inside it, ProseMirror would take the copies for edits),
 * which carries the editor's classes so the copies get the same fonts and spacing.
 */
export function fittedColumnWidth(view: EditorView, cellPos: number): number | null {
  const { table, start, col } = columnOf(view, cellPos);
  const tableDom = tableDomOf(view, start);
  const host = view.dom.parentElement;
  if (!tableDom || !host) return null;

  const map = TableMap.get(table);
  const probeTable = tableDom.cloneNode(false) as HTMLTableElement;
  probeTable.removeAttribute('style');
  probeTable.style.cssText = 'width:max-content;min-width:0;max-width:none;table-layout:auto;margin:0;';
  const tbody = document.createElement('tbody');
  probeTable.appendChild(tbody);

  const seen = new Set<number>();
  for (let row = 0; row < map.height; row++) {
    const pos = map.map[row * map.width + col];
    if (seen.has(pos)) continue;
    seen.add(pos);
    if (table.nodeAt(pos)?.attrs['colspan'] !== 1) continue;
    const cellDom = view.nodeDOM(start + pos);
    if (!(cellDom instanceof HTMLElement)) continue;
    const copy = cellDom.cloneNode(true) as HTMLElement;
    copy.querySelectorAll('.column-resize-handle').forEach((el) => el.remove());
    copy.removeAttribute('colspan');
    copy.removeAttribute('rowspan');
    copy.style.width = 'auto';
    copy.style.minWidth = '0';
    copy.style.maxWidth = 'none';
    // 'pre' rather than 'nowrap': the editor keeps runs of spaces, so the copy must too.
    for (const el of [copy, ...Array.from(copy.querySelectorAll<HTMLElement>('*'))]) el.style.whiteSpace = 'pre';
    const tr = document.createElement('tr');
    tr.appendChild(copy);
    tbody.appendChild(tr);
  }
  if (!tbody.firstChild) return null;

  const probe = document.createElement('div');
  probe.className = view.dom.className;
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText =
    'position:absolute;left:0;top:0;visibility:hidden;pointer-events:none;width:max-content;max-width:none;height:auto;min-height:0;padding:0;overflow:visible;';
  const wrapper = document.createElement('div');
  wrapper.className = 'tableWrapper';
  wrapper.style.cssText = 'overflow:visible;width:max-content;margin:0;padding:0;';
  wrapper.appendChild(probeTable);
  probe.appendChild(wrapper);
  host.appendChild(probe);
  // offsetWidth is the layout width, unaffected by the app's zoom transform — the unit colwidth uses.
  const width = (tbody.querySelector('td, th') as HTMLElement | null)?.offsetWidth ?? 0;
  probe.remove();
  return width > 0 ? Math.max(CELL_MIN_WIDTH, width + 1) : null;
}

/** Fits the column of the cell at `cellPos` to its contents. Returns whether the width was set. */
export function fitColumnToContent(view: EditorView, cellPos: number): boolean {
  const width = fittedColumnWidth(view, cellPos);
  if (width === null) return false;
  setColumnWidth(view, cellPos, width);
  return true;
}

/** The cell whose right border is under the pointer (the mouse resize handle), or -1. */
function borderCellAt(view: EditorView, event: MouseEvent): number {
  const handle = (columnResizingPluginKey.getState(view.state) as { activeHandle?: number } | undefined)?.activeHandle;
  if (typeof handle === 'number' && handle > -1) return handle;
  const cellDom = domCellAround(event.target);
  return cellDom ? touchedEdgeCell(view, cellDom, event.clientX, MOUSE_EDGE_PX) : -1;
}

export const TableColumnAutoFit = Extension.create({
  name: 'tableColumnAutoFit',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: tableColumnAutoFitKey,
        props: {
          handleDOMEvents: {
            dblclick: (view, event) => {
              if (!view.editable) return false;
              const cellPos = borderCellAt(view, event);
              if (cellPos < 0 || !fitColumnToContent(view, cellPos)) return false;
              event.preventDefault();
              return true;
            },
          },
        },
        view: (view) => {
          // A finger press sends no dblclick here (the touch resize handles the press), so two quick taps
          // on the same border are detected instead.
          let press: { cellPos: number; x: number; y: number; moved: boolean } | null = null;
          let lastTap: { cellPos: number; time: number } | null = null;

          const onTouchStart = (event: TouchEvent) => {
            press = null;
            if (!view.editable || event.touches.length !== 1) return;
            const touch = event.touches[0];
            const cellDom = domCellAround(event.target);
            const cellPos = cellDom ? touchedEdgeCell(view, cellDom, touch.clientX, TABLE_TOUCH_EDGE_PX) : -1;
            if (cellPos < 0) {
              lastTap = null;
              return;
            }
            press = { cellPos, x: touch.clientX, y: touch.clientY, moved: false };
          };

          const onTouchMove = (event: TouchEvent) => {
            if (!press) return;
            const touch = event.touches[0];
            if (event.touches.length !== 1 || Math.hypot(touch.clientX - press.x, touch.clientY - press.y) > TAP_SLOP_PX) press.moved = true;
          };

          const onTouchEnd = (event: TouchEvent) => {
            const tap = press;
            press = null;
            if (!tap || tap.moved) {
              lastTap = null;
              return;
            }
            const now = Date.now();
            if (lastTap && lastTap.cellPos === tap.cellPos && now - lastTap.time <= DOUBLE_TAP_MS) {
              lastTap = null;
              // No caret placement / double-tap zoom for the second tap.
              if (fitColumnToContent(view, tap.cellPos) && event.cancelable) event.preventDefault();
              return;
            }
            lastTap = { cellPos: tap.cellPos, time: now };
          };

          view.dom.addEventListener('touchstart', onTouchStart, { passive: true });
          view.dom.addEventListener('touchmove', onTouchMove, { passive: true });
          view.dom.addEventListener('touchend', onTouchEnd);

          return {
            destroy: () => {
              view.dom.removeEventListener('touchstart', onTouchStart);
              view.dom.removeEventListener('touchmove', onTouchMove);
              view.dom.removeEventListener('touchend', onTouchEnd);
            },
          };
        },
      }),
    ];
  },
});
